// Helpers for the RFC 9457 problem+json bodies the API returns as `error`.

/** A 401 from a data call — the caller redirects to /login. */
export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "UnauthorizedError";
  }
}

/** Safely pull a human-readable title out of an unknown problem body.
 *
 * Handles BOTH shapes a caller can be holding: a raw RFC 9457 body (`title`
 * at the top level — the openapi-fetch `error` member), and a thrown
 * `RequestError`, whose problem lives at `.problem` and whose `message` IS
 * the problem title (lib/request.ts). The M07-S10/S11 certification proved
 * the second shape was invisible here: every mutation refusal rendered the
 * generic fallback instead of the server's own sentence. Structural checks,
 * not `instanceof` — request.ts imports this file, so the type cannot. */
export function problemTitle(err: unknown): string | undefined {
  if (err === null || typeof err !== "object") return undefined;
  if ("title" in err) {
    const { title } = err as { title?: unknown };
    if (typeof title === "string" && title.length > 0) return title;
  }
  if ("problem" in err) {
    const problem = (err as { problem?: unknown }).problem;
    if (problem !== null && typeof problem === "object" && "title" in problem) {
      const { title } = problem as { title?: unknown };
      if (typeof title === "string" && title.length > 0) return title;
    }
  }
  if (err instanceof Error && err.name === "RequestError" && err.message.length > 0) {
    return err.message;
  }
  return undefined;
}
